import * as React from 'react';
import { useState } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import { Button, Result, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import posthog from 'posthog-js';
import NotificationActionWrapper from '../notificationActions/NotificationActionWrapper';
import { fetchG2ReviewToken, G2ReviewTokenResponse } from '../../thunks/g2_thunks';
import { buildG2IframeUrl } from '../../utils/G2Utils';
import { getHomePageByRole } from '../../utils/EnvConfigUtils';
import { withUserType } from '../withUserType';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import useMountEffect from '../../hooks/useMountEffect';

interface G2ReviewPageProps extends RouteComponentProps {
	currentUser: { email?: string } | null;
	userType: string;
	featureFlags: any;
	fetchG2ReviewToken: (email: string) => Promise<G2ReviewTokenResponse>;
}

type Status = 'loading' | 'open' | 'alreadyReviewed' | 'error' | 'noEmail';

const G2ReviewPage: React.FC<G2ReviewPageProps> = (props) => {
	const { currentUser, userType, featureFlags, fetchG2ReviewToken, history } = props;
	const [status, setStatus] = useState<Status>('loading');
	const [iframeUrl, setIframeUrl] = useState('');

	const goToHomePage = () => {
		posthog.capture('g2_review_go_home_clicked', {
			source: 'dedicated_page',
			user_type: userType,
			page_status: status,
		});
		history.push(getHomePageByRole(userType, featureFlags));
	};

	useMountEffect(() => {
		const telemetryProps = { source: 'dedicated_page', user_type: userType };
		posthog.capture('g2_review_page_viewed', telemetryProps);

		const email = nullSafeGet('email', currentUser);
		if (!email) {
			posthog.capture('g2_review_load_failed', { ...telemetryProps, reason: 'no_email' });
			setStatus('noEmail');
			return;
		}
		let cancelled = false;

		fetchG2ReviewToken(email)
			.then((result) => {
				if (cancelled) {
					return;
				}
				if (result.reviewExists) {
					posthog.capture('g2_review_already_exists', telemetryProps);
					setStatus('alreadyReviewed');
					return;
				}
				if (result.state) {
					posthog.capture('g2_review_token_issued', telemetryProps);
					setIframeUrl(buildG2IframeUrl(result.state, email));
					setStatus('open');
				} else {
					posthog.capture('g2_review_load_failed', { ...telemetryProps, reason: 'no_state' });
					setStatus('error');
				}
			})
			.catch(() => {
				if (cancelled) {
					return;
				}
				posthog.capture('g2_review_load_failed', {
					...telemetryProps,
					reason: 'token_fetch_failed',
				});
				setStatus('error');
			});

		return () => {
			cancelled = true;
		};
	});

	return (
		<>
			<div className="fixed inset-x-0 top-0 z-20 flex h-16 items-center px-6">
				<Button
					type="link"
					onClick={goToHomePage}
					icon={<ArrowLeftOutlined />}
					className="bodymedium flex items-center !text-white"
				>
					Home
				</Button>
			</div>
			<NotificationActionWrapper cardStyle={status === 'open' ? { width: 760 } : undefined}>
				{status === 'loading' ? (
					<Spin size="large" />
				) : status === 'open' ? (
					<iframe
						src={iframeUrl}
						title="G2 Review Form"
						width="100%"
						height="550px"
						sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
						className="border-none"
						onLoad={() =>
							posthog.capture('g2_review_form_loaded', {
								source: 'dedicated_page',
								user_type: userType,
							})
						}
					/>
				) : status === 'alreadyReviewed' ? (
					<Result
						status="success"
						title="Thanks — you've already left us a review on G2!"
						subTitle="We appreciate you taking the time to share your experience."
					/>
				) : (
					<Result
						status="warning"
						title="We couldn't load the review form"
						subTitle="Please try again later or reach out to your OpenWrench contact."
					/>
				)}
			</NotificationActionWrapper>
		</>
	);
};

const mapStateToProps = (state: any) => ({
	currentUser: state.session.currentUser,
	featureFlags: state.session.featureFlags,
});

const mapDispatchToProps = (dispatch: any, ownProps: { userType: string }) => ({
	fetchG2ReviewToken: (email: string) => dispatch(fetchG2ReviewToken(ownProps.userType, email)),
});

export default withUserType(withRouter(connect(mapStateToProps, mapDispatchToProps)(G2ReviewPage)));
